import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn } from "lucide-react";
import { authApi, type LoginRequest } from "../api/authApi";
import { Card } from "../components/layout/Card";
import { useSession } from "../hooks/useSession";

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, refresh } = useSession();

  // Keep the (rare) customerId that older portals pass in ?customerid=... so we
  // reuse the same multi-tenant path the existing portal supports today.
  const customerIdParam = params.get("customerid") ?? "";
  const reason = params.get("reason");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [customerId, setCustomerId] = useState(customerIdParam);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const expiredMessage = useMemo(() => {
    if (reason === "expired") return "Your session expired. Please sign in again.";
    return null;
  }, [reason]);

  useEffect(() => {
    if (session?.isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [session, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const request: LoginRequest = {
      username,
      password,
      customerId: customerId || null,
      rememberMe,
    };

    try {
      const response = await authApi.login(request);
      if (!response.ok) {
        setError(response.error ?? "Sign in failed.");
        return;
      }

      await refresh();

      if (response.session?.redirectToManagerId && response.session.redirectToManagerId > 0) {
        // Whitelabel flow will be built out in a later iteration; for now give a clear message.
        setError("Your account belongs to another portal. Please contact support.");
        return;
      }

      if (response.session?.checkSiblings) {
        navigate("/select-customer", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-shell">
      <Card>
        <div className="auth-card">
          <h2>Sign in</h2>
          <p className="sub">Use your email address or username.</p>

          {expiredMessage && <div className="alert alert-info">{expiredMessage}</div>}
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            {customerIdParam && (
              <div className="field">
                <label htmlFor="customerId">Customer ID</label>
                <input
                  id="customerId"
                  type="text"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                />
                <div className="hint">Auto-filled from the link.</div>
              </div>
            )}
            <div className="field">
              <label htmlFor="username">Username or email</label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me on this device
            </label>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <LogIn size={16} /> {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p style={{ marginTop: 16, fontSize: "0.9rem", color: "var(--portal-muted)" }}>
            <Link to="/forgot-password">Forgot password?</Link>
          </p>
        </div>
      </Card>
    </section>
  );
}
